import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Camera,
  Video,
  Wifi,
  WifiOff,
  Activity,
  Clock,
  Car,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowUpDown,
  LayoutDashboard,
  FileText,
  Building,
  Store,
  Gift,
  BarChart3,
  Shield,
  Briefcase,
  DollarSign,
  TrendingUp,
  Monitor,
  MapPin,
  LogOut,
  ChevronRight,
  ImageOff,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Layers,
  HardDrive,
  Wallet,
  Package,
  Zap,
  Target
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';

const API = '/api';

// Direction display config
const directionConfig = {
  entry: { label: 'Entry', color: 'bg-blue-100 text-blue-800', icon: ArrowDownToLine },
  exit: { label: 'Exit', color: 'bg-green-100 text-green-800', icon: ArrowUpFromLine },
  both: { label: 'Combo', color: 'bg-purple-100 text-purple-800', icon: ArrowUpDown },
};

// Decision display config - Business-approved terminology only
// BLOCKED: 25-second Combo anti-retrigger ONLY (HikVision Combo Exit sees same vehicle after Combo Entry)
// NO SESSION (FREE EXIT): No session found
// CHARGE: Payment required  
// PAID EXIT: Paid
// FREE EXIT: Free/whitelist/permit
// LEGACY BLOCK / REVIEW REQUIRED: Any other old DENY-style output
const decisionConfig = {
  ALLOW: { label: 'ALLOW', color: 'bg-green-100 text-green-800' },
  BLOCKED: { label: 'BLOCKED', color: 'bg-gray-100 text-gray-800' }, // 25-second Combo ONLY
  LEGACY_BLOCK: { label: 'LEGACY BLOCK / REVIEW REQUIRED', color: 'bg-red-100 text-red-800' },
  PENDING: { label: 'PENDING', color: 'bg-yellow-100 text-yellow-800' },
  FREE_EXIT: { label: 'FREE EXIT', color: 'bg-green-100 text-green-800' },
  PAID_EXIT: { label: 'PAID EXIT', color: 'bg-blue-100 text-blue-800' },
  CHARGE: { label: 'CHARGE', color: 'bg-orange-100 text-orange-800' },
  NO_SESSION_FREE_EXIT: { label: 'NO SESSION (FREE EXIT)', color: 'bg-amber-100 text-amber-800' },
  // Exit reason types
  WHITELISTED: { label: 'WHITELISTED', color: 'bg-purple-100 text-purple-800' },
  PERMIT: { label: 'PERMIT', color: 'bg-indigo-100 text-indigo-800' },
  STAFF: { label: 'STAFF', color: 'bg-cyan-100 text-cyan-800' },
  NHS: { label: 'NHS', color: 'bg-teal-100 text-teal-800' },
  BLUE_BADGE: { label: 'BLUE BADGE', color: 'bg-sky-100 text-sky-800' },
  RESIDENT: { label: 'RESIDENT', color: 'bg-violet-100 text-violet-800' },
};

// Normalize decision to business terminology
// BLOCKED is ONLY for 25-second Combo anti-retrigger
function normalizeDecision(decision, cameraId = null, direction = null) {
  if (!decision) return null;
  const upper = decision.toUpperCase();
  
  // Handle legacy DENY
  if (upper === 'DENY' || upper === 'DENIED') {
    // Check if this is the 25-second Combo anti-retrigger case
    const isComboCamera = cameraId && (
      cameraId.toLowerCase().includes("combo") || 
      cameraId.includes("235d49")
    );
    
    if (isComboCamera && direction === "exit") {
      return 'BLOCKED'; // ONLY valid BLOCKED case
    } else if (direction === "exit") {
      return 'NO_SESSION_FREE_EXIT';
    } else {
      return 'LEGACY_BLOCK'; // Needs review
    }
  }
  
  return upper;
}

// Physical camera inventory (known hardware)
const PHYSICAL_CAMERAS = [
  { ip: '10.10.10.200', name: 'Entry Camera', role: 'entry', logical_id: '180300235d32' },
  { ip: '10.10.10.201', name: 'Exit Camera', role: 'exit', logical_id: '180300235d60' },
  { ip: '10.10.10.203', name: 'Combo Entry Camera', role: 'combo_entry', logical_id: '180300235d49' },
  { ip: '10.10.10.204', name: 'Combo Exit Camera', role: 'combo_exit', logical_id: null, merged_into: '180300235d49' },
];

// Test VRM patterns - these indicate test images, not live captures
const TEST_VRM_PATTERNS = [
  /^TEST/i, /^SNAP/i, /^DEBUG/i, /^LIVE\d/i, /^MULT/i, /^PIPE/i, 
  /^FIX/i, /^FINAL/i, /^COMBO\d/i, /^EXIT\d/i, /^ENTRY\d/i,
  /^PAY/i, /^FEE/i, /^AUTH/i, /^DENY/i, /^RULE/i, /^FULL/i,
  /^SIM/i, /^OVER/i, /^JSON/i, /^REG/i, /^EV/i, /^V4/i, /^RAW/i,
  /^CLEAN/i, /^VPS/i, /^PKG/i, /^CMB/i, /^AX/i, /^FLOW/i,
  /\d{5,}$/ // VRMs ending with 5+ digits are likely test
];

function isTestVRM(vrm) {
  if (!vrm) return false;
  return TEST_VRM_PATTERNS.some(pattern => pattern.test(vrm));
}

function formatTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getTimeSince(isoString) {
  if (!isoString) return 'Never';
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function getCameraStatus(lastSeen) {
  if (!lastSeen) return { status: 'offline', label: 'Offline', color: 'bg-gray-100 text-gray-600' };
  const now = new Date();
  const then = new Date(lastSeen);
  const diffMins = Math.floor((now - then) / 60000);
  
  if (diffMins < 5) return { status: 'online', label: 'Online', color: 'bg-green-100 text-green-800' };
  if (diffMins < 30) return { status: 'idle', label: 'Idle', color: 'bg-yellow-100 text-yellow-800' };
  return { status: 'offline', label: 'Offline', color: 'bg-red-100 text-red-800' };
}

export default function AdminCameras() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [healthData, setHealthData] = useState({});  // Phase 1: Camera health data
  const [events, setEvents] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [cameraEvents, setCameraEvents] = useState([]);
  const [showDetailPanel, setShowDetailPanel] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchCameras();
      fetchHealth();  // Phase 1: Fetch health data
      fetchEvents();
    }
  }, [user, selectedSite]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchSites();
    } catch (error) {
      navigate('/admin/login');
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(response.data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchCameras = async () => {
    try {
      // Use new activity endpoint that queries trace_ledger for reliable status
      const siteParam = selectedSite !== 'all' ? `?site_id=${selectedSite}` : '';
      const response = await axios.get(`${API}/cameras/activity${siteParam}`, { withCredentials: true });
      setCameras(response.data.cameras || []);
    } catch (error) {
      console.error('Failed to fetch cameras:', error);
      setCameras([]);
    } finally {
      setLoading(false);
    }
  };

  // Phase 1: Fetch camera health data from ANPR health endpoint
  const fetchHealth = async () => {
    try {
      const response = await axios.get(`${API}/anpr/health/cameras?hours=1`, { withCredentials: true });
      setHealthData(response.data.cameras || {});
    } catch (error) {
      console.error('Failed to fetch camera health:', error);
      setHealthData({});  // Graceful fallback
    }
  };

  const fetchEvents = async () => {
    // Events are now fetched per-camera when selecting a camera
    // No longer needed for initial load
  };

  const handleCameraSelect = async (camera) => {
    setSelectedCamera(camera);
    setShowDetailPanel(true);
    
    // Fetch events for this camera from trace_ledger
    try {
      const response = await axios.get(`${API}/cameras/activity/${camera.camera_id}/events?limit=20`, { withCredentials: true });
      setCameraEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch camera events:', error);
      setCameraEvents([]);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Logout error can be ignored
    }
    navigate('/admin/login');
  };

  // Calculate stats from activity endpoint data
  const totalCameras = cameras.length;
  const onlineCameras = cameras.filter(c => {
    return getCameraStatus(c.last_seen).status === 'online';
  }).length;
  const offlineCameras = totalCameras - onlineCameras;
  
  // Today's events - sum from all cameras
  const eventsToday = cameras.reduce((sum, c) => sum + (c.events_today || 0), 0);

  // Phase 1: Calculate health stats from health endpoint
  const healthCameras = Object.values(healthData);
  const activeCameras1h = healthCameras.filter(h => h.status === 'active').length;
  const avgConfidence = healthCameras.length > 0 
    ? Math.round(healthCameras.reduce((sum, h) => sum + (h.avg_confidence || 0), 0) / healthCameras.length)
    : null;

  // Phase 1: Get health status badge config
  const getHealthBadge = (cameraId) => {
    const health = healthData[cameraId];
    if (!health) return { label: 'No Data', color: 'bg-gray-100 text-gray-600', icon: null };
    if (health.status === 'active') return { label: 'Active', color: 'bg-green-100 text-green-800', icon: 'active' };
    if (health.status === 'idle') return { label: 'Idle', color: 'bg-yellow-100 text-yellow-800', icon: 'idle' };
    return { label: 'Inactive', color: 'bg-red-100 text-red-800', icon: 'inactive' };
  };

  // Phase 1: Get confidence color
  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-green-400';
    if (confidence >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  // Cameras already enriched from activity endpoint + Phase 1 health data
  const enrichedCameras = cameras.map(camera => {
    const isMergedIdentity = camera.camera_id === '180300235d49';
    const health = healthData[camera.camera_id] || {};
    return {
      ...camera,
      last_event: camera.last_seen,
      last_plate: camera.last_vrm,
      status: getCameraStatus(camera.last_seen),
      is_merged_identity: isMergedIdentity,
      display_name: isMergedIdentity ? 'Combo Logical Lane' : (camera.name || camera.camera_id),
      // Phase 1: Health data
      health_status: health.status || 'unknown',
      avg_confidence: health.avg_confidence || null,
      events_1h: health.events_count || 0,
      unique_vrms_1h: health.unique_vrms || 0
    };
  });

  // Build physical camera status by matching to logical identities
  const physicalCameraStatus = PHYSICAL_CAMERAS.map(pc => {
    const logicalCamera = cameras.find(c => c.camera_id === pc.logical_id);
    const isMerged = pc.merged_into !== undefined;
    const mergedIntoCamera = isMerged ? cameras.find(c => c.camera_id === pc.merged_into) : null;
    
    return {
      ...pc,
      logical_camera: logicalCamera,
      status: isMerged 
        ? { status: 'merged', label: 'Identity Not Separated', color: 'bg-orange-100 text-orange-800' }
        : (logicalCamera ? getCameraStatus(logicalCamera.last_seen) : { status: 'unknown', label: 'Unknown', color: 'bg-gray-100 text-gray-600' }),
      last_seen: logicalCamera?.last_seen,
      events_today: logicalCamera?.events_today || 0,
      merged_into_camera: mergedIntoCamera
    };
  });

  if (loading) {
    return (
      <div className="admin-container flex items-center justify-center min-h-screen bg-slate-950">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="admin-container min-h-screen bg-slate-950" data-testid="admin-cameras">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        {/* Site Selector */}
        <div className="mb-6">
          <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Site Filter</label>
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white" data-testid="site-selector">
              <MapPin className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Sites</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Navigation */}
        <nav className="space-y-1">
          <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link to="/admin/invoices" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <FileText className="w-5 h-5" />
            Invoices
          </Link>
          <Link to="/admin/whitelist" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <Shield className="w-5 h-5" />
            Whitelists
          </Link>
          <Link to="/admin/advertising" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <Store className="w-5 h-5" />
            Advertising
          </Link>
          <Link to="/admin/designated-spaces" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <MapPin className="w-5 h-5" />
            Designated Spaces
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <BarChart3 className="w-5 h-5" />
            Analytics
          </Link>
          <Link to="/admin/cameras" className="flex items-center gap-3 px-3 py-2.5 text-white bg-slate-800 rounded-lg">
            <Camera className="w-5 h-5 text-blue-400" />
            Cameras
          </Link>
          
          {/* Commercial Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-3 mb-2">Commercial</p>
            <Link to="/admin/commercial" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
              <Wallet className="w-5 h-5" />
              Commercial Dashboard
            </Link>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
              <DollarSign className="w-5 h-5" />
              Finance Centre
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
              <Package className="w-5 h-5" />
              Products & Tariffs
            </Link>
          </div>
        </nav>

        {/* User Info */}
        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {user?.name?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Camera Dashboard</h1>
          <p className="text-slate-400">Monitor camera status and ANPR activity</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <Card 
            className="bg-slate-900 border-slate-800 cursor-pointer hover:border-slate-600 transition-colors"
            onClick={() => {}}
            data-testid="card-total-cameras"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm mb-1">Total Cameras</p>
                  <p className="text-3xl font-bold text-white">{totalCameras}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-slate-900 border-slate-800 cursor-pointer hover:border-slate-600 transition-colors"
            onClick={() => {}}
            data-testid="card-online-cameras"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm mb-1">Online Cameras</p>
                  <p className="text-3xl font-bold text-green-400">{onlineCameras}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-slate-900 border-slate-800 cursor-pointer hover:border-slate-600 transition-colors"
            onClick={() => {}}
            data-testid="card-offline-cameras"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm mb-1">Offline Cameras</p>
                  <p className="text-3xl font-bold text-red-400">{offlineCameras}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <WifiOff className="w-6 h-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-slate-900 border-slate-800 cursor-pointer hover:border-slate-600 transition-colors"
            onClick={() => {}}
            data-testid="card-events-today"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm mb-1">Events Today</p>
                  <p className="text-3xl font-bold text-purple-400">{eventsToday}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Phase 1: Health Stats Row */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <Card 
            className="bg-slate-900 border-slate-800"
            data-testid="card-active-cameras-1h"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm mb-1">Active Cameras (1h)</p>
                  <p className="text-3xl font-bold text-cyan-400">{activeCameras1h}</p>
                  <p className="text-slate-500 text-xs mt-1">From health endpoint</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-cyan-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-slate-900 border-slate-800"
            data-testid="card-avg-confidence"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm mb-1">Avg Confidence (1h)</p>
                  <p className={`text-3xl font-bold ${avgConfidence !== null ? getConfidenceColor(avgConfidence) : 'text-slate-500'}`}>
                    {avgConfidence !== null ? `${avgConfidence}%` : 'N/A'}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">ANPR read confidence</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Target className="w-6 h-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Physical Camera Inventory */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-orange-400" />
              Physical Camera Inventory
              <Badge className="ml-2 bg-slate-700 text-slate-300 text-xs">Hardware</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-slate-800/50">
                  <TableHead className="text-slate-400">Physical Device</TableHead>
                  <TableHead className="text-slate-400">IP Address</TableHead>
                  <TableHead className="text-slate-400">Role</TableHead>
                  <TableHead className="text-slate-400">Identity Status</TableHead>
                  <TableHead className="text-slate-400">Logical ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {physicalCameraStatus.map((camera) => (
                  <TableRow 
                    key={camera.ip}
                    className="border-slate-800 hover:bg-slate-800/50"
                    data-testid={`physical-camera-${camera.ip.replace(/\./g, '-')}`}
                  >
                    <TableCell className="text-white font-medium">
                      {camera.name}
                    </TableCell>
                    <TableCell className="text-slate-300 font-mono text-sm">
                      {camera.ip}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        camera.role === 'entry' ? 'bg-blue-100 text-blue-800' :
                        camera.role === 'exit' ? 'bg-green-100 text-green-800' :
                        camera.role === 'combo_entry' ? 'bg-purple-100 text-purple-800' :
                        'bg-orange-100 text-orange-800'
                      }>
                        {camera.role.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {camera.merged_into ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-orange-100 text-orange-800">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Identity Not Separated
                          </Badge>
                        </div>
                      ) : (
                        <Badge className={camera.status.color}>
                          {camera.status.status === 'online' && <Wifi className="w-3 h-3 mr-1" />}
                          {camera.status.status === 'offline' && <WifiOff className="w-3 h-3 mr-1" />}
                          {camera.status.label}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300 font-mono text-sm">
                      {camera.merged_into ? (
                        <span className="text-orange-400">Merged → {camera.merged_into}</span>
                      ) : (
                        camera.logical_id || '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-orange-400 font-medium">Identity Reconciliation Required</p>
                  <p className="text-slate-400 mt-1">
                    10.10.10.204 (Combo Exit Camera) is not independently tracked. Its events are currently merged 
                    into logical identity <code className="bg-slate-800 px-1 rounded">180300235d49</code>. 
                    Physical camera health cannot be monitored separately until identity is reconciled.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logical Lane Activity Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-400" />
              Logical Lane Activity
              <Badge className="ml-2 bg-slate-700 text-slate-300 text-xs">From trace_ledger</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-slate-800/50">
                  <TableHead className="text-slate-400">Logical Identity</TableHead>
                  <TableHead className="text-slate-400">Camera ID</TableHead>
                  <TableHead className="text-slate-400">IP Address</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Health (1h)</TableHead>
                  <TableHead className="text-slate-400">Confidence</TableHead>
                  <TableHead className="text-slate-400">Direction</TableHead>
                  <TableHead className="text-slate-400">Last Plate</TableHead>
                  <TableHead className="text-slate-400">Last Seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrichedCameras.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-slate-500 py-12">
                      No cameras configured
                    </TableCell>
                  </TableRow>
                ) : (
                  enrichedCameras.map((camera) => {
                    const dirConfig = directionConfig[camera.direction] || directionConfig.entry;
                    const DirIcon = dirConfig.icon;
                    
                    return (
                      <TableRow 
                        key={camera.camera_id}
                        className="border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                        onClick={() => handleCameraSelect(camera)}
                        data-testid={`camera-row-${camera.camera_id}`}
                      >
                        <TableCell className="text-white font-medium">
                          <div className="flex items-center gap-2">
                            {camera.display_name}
                            {camera.is_merged_identity && (
                              <Badge className="bg-orange-100 text-orange-800 text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Merged
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300 font-mono text-sm">
                          {camera.camera_id}
                        </TableCell>
                        <TableCell className="text-slate-300 font-mono text-sm">
                          {camera.ip_address || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={camera.status.color}>
                            {camera.status.status === 'online' ? (
                              <Wifi className="w-3 h-3 mr-1" />
                            ) : (
                              <WifiOff className="w-3 h-3 mr-1" />
                            )}
                            {camera.status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {/* Phase 1: Health badge */}
                          <Badge className={getHealthBadge(camera.camera_id).color}>
                            {getHealthBadge(camera.camera_id).label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {/* Phase 1: Confidence display */}
                          {camera.avg_confidence !== null ? (
                            <span className={`font-medium ${getConfidenceColor(camera.avg_confidence)}`}>
                              {Math.round(camera.avg_confidence)}%
                            </span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={dirConfig.color}>
                            <DirIcon className="w-3 h-3 mr-1" />
                            {dirConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300 font-mono">
                          {camera.last_plate || '-'}
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {getTimeSince(camera.last_seen)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            {enrichedCameras.some(c => c.is_merged_identity) && (
              <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-orange-400 font-medium">Merged Identity Warning</p>
                    <p className="text-slate-400 mt-1">
                      <code className="bg-slate-800 px-1 rounded">180300235d49</code> is a logical lane identity that 
                      contains activity from both Combo Entry (10.10.10.203) and Combo Exit (10.10.10.204) cameras. 
                      Direction shown is post-override. Events from both physical cameras appear under this single identity.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Camera Detail Panel */}
      <Dialog open={showDetailPanel} onOpenChange={setShowDetailPanel}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-3">
              <Camera className="w-6 h-6 text-blue-400" />
              {selectedCamera?.display_name || selectedCamera?.camera_id}
              {selectedCamera?.is_merged_identity && (
                <Badge className="bg-orange-100 text-orange-800 text-sm">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Merged Identity
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCamera && (
            <div className="space-y-6 mt-4">
              {/* Merged Identity Warning */}
              {selectedCamera.is_merged_identity && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-orange-400 font-medium">Combo Logical Lane</p>
                      <p className="text-slate-400 text-sm mt-1">
                        This identity contains merged activity from both physical cameras:
                      </p>
                      <ul className="text-slate-400 text-sm mt-2 space-y-1">
                        <li>• 10.10.10.203 (Combo Entry Camera)</li>
                        <li>• 10.10.10.204 (Combo Exit Camera) — Identity not separated</li>
                      </ul>
                      <p className="text-slate-400 text-sm mt-2">
                        All events have direction forced to <code className="bg-slate-800 px-1 rounded">exit</code> via HIKVISION_DIRECTION_OVERRIDE.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Camera Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Camera ID</p>
                  <p className="text-white font-mono">{selectedCamera.camera_id}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">IP Address</p>
                  <p className="text-white font-mono">{selectedCamera.ip_address || '-'}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Direction</p>
                  <Badge className={directionConfig[selectedCamera.direction]?.color || 'bg-slate-700'}>
                    {directionConfig[selectedCamera.direction]?.label || selectedCamera.direction}
                  </Badge>
                </div>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Status</p>
                  <Badge className={selectedCamera.status?.color || 'bg-slate-700'}>
                    {selectedCamera.status?.label || 'Unknown'}
                  </Badge>
                </div>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Last Seen</p>
                  <p className="text-white">{getTimeSince(selectedCamera.last_seen)}</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Last Plate</p>
                  <p className="text-white font-mono text-lg">{selectedCamera.last_plate || '-'}</p>
                </div>
              </div>

              {/* Health Status */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center bg-blue-500/20">
                    <Clock className="w-8 h-8 text-blue-400" />
                  </div>
                  <p className="text-slate-400 text-sm">Last Event</p>
                  <p className="text-white font-medium">{formatTime(selectedCamera.last_seen)}</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center bg-green-500/20">
                    {selectedCamera.status?.status === 'online' ? (
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    ) : selectedCamera.status?.status === 'idle' ? (
                      <AlertTriangle className="w-8 h-8 text-yellow-400" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-400" />
                    )}
                  </div>
                  <p className="text-slate-400 text-sm">Connection</p>
                  <p className="text-white font-medium">{selectedCamera.status?.label}</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center bg-purple-500/20">
                    <Activity className="w-8 h-8 text-purple-400" />
                  </div>
                  <p className="text-slate-400 text-sm">Events Today</p>
                  <p className="text-white font-medium">
                    {selectedCamera.events_today || 0}
                  </p>
                </div>
              </div>

              {/* Snapshot Panel */}
              <div className="bg-slate-800 rounded-lg p-4">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <ImageOff className="w-4 h-4 text-slate-400" />
                  Latest Snapshot
                </h3>
                <div className="bg-slate-950 rounded-lg h-48 flex items-center justify-center border border-slate-700 overflow-hidden">
                  {selectedCamera?.latest_snapshot?.image_base64 ? (
                    <div className="w-full h-full relative flex items-center justify-center bg-slate-950">
                      <img 
                        src={
                          selectedCamera.latest_snapshot.image_base64.startsWith('data:image')
                            ? selectedCamera.latest_snapshot.image_base64
                            : `data:image/jpeg;base64,${selectedCamera.latest_snapshot.image_base64}`
                        } 
                        alt={`Snapshot of ${selectedCamera.latest_snapshot.vrm}`}
                        className="max-w-full max-h-full object-contain"
                        style={{ maxHeight: 'calc(100% - 24px)' }}
                        data-testid="camera-snapshot-image"
                        onError={(e) => {
                          console.error('Image failed to load:', selectedCamera.latest_snapshot.image_base64?.substring(0, 100));
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/80 px-3 py-1.5 text-xs flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-mono">{selectedCamera.latest_snapshot.vrm}</span>
                          {isTestVRM(selectedCamera.latest_snapshot.vrm) && (
                            <Badge className="bg-yellow-500 text-yellow-900 text-xs" data-testid="test-image-badge">
                              TEST IMAGE
                            </Badge>
                          )}
                        </div>
                        <span className="text-slate-400">{formatDateTime(selectedCamera.latest_snapshot.timestamp)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-slate-500">
                      <ImageOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No Snapshot Available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Events */}
              <div className="bg-slate-800 rounded-lg p-4">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-slate-400" />
                  Recent ANPR Events
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-slate-700/50">
                      <TableHead className="text-slate-400">Time</TableHead>
                      <TableHead className="text-slate-400">VRM</TableHead>
                      <TableHead className="text-slate-400">Direction</TableHead>
                      <TableHead className="text-slate-400">Decision</TableHead>
                      <TableHead className="text-slate-400">Barrier</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cameraEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-6">
                          No recent events
                        </TableCell>
                      </TableRow>
                    ) : (
                      cameraEvents.map((event, idx) => (
                        <TableRow key={event.trace_id || idx} className="border-slate-700">
                          <TableCell className="text-slate-300">
                            {formatTime(event.timestamp)}
                          </TableCell>
                          <TableCell className="text-white font-mono font-medium">
                            {event.vehicle_registration}
                          </TableCell>
                          <TableCell>
                            <Badge className={directionConfig[event.direction]?.color || 'bg-slate-700'}>
                              {event.direction?.toUpperCase() || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const normalized = normalizeDecision(event.decision, event.camera_id, event.direction);
                              return (
                                <Badge className={decisionConfig[normalized]?.color || 'bg-slate-700 text-slate-300'}>
                                  {decisionConfig[normalized]?.label || normalized || '-'}
                                </Badge>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-slate-300 font-mono text-sm">
                            {event.barrier_id || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
